import { Link } from "react-router-dom";
import type { Topic } from "@/types/content";
import { formatViews, heatLabel } from "@/utils/format";

interface TopicCardProps {
  topic: Topic;
  variant?: "default" | "featured" | "compact";
}

function HeatTag({ heat }: { heat: number }) {
  return (
    <span className="flex items-center gap-1 rounded-md bg-primary-100 px-2 py-1 text-[11px] font-medium text-primary-700">
      <span className="flex h-3 w-3 items-center justify-center">
        <i className="ri-fire-fill text-[11px]" />
      </span>
      {heatLabel(heat)}
    </span>
  );
}

export default function TopicCard({ topic, variant = "default" }: TopicCardProps) {
  const to = `/topic/${topic.id}`;

  if (variant === "compact") {
    return (
      <article className="group flex gap-3">
        <Link
          to={to}
          className="relative h-[72px] w-[96px] shrink-0 overflow-hidden rounded-lg bg-background-200"
        >
          <img
            src={topic.cover}
            alt={topic.coverAlt}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-col justify-between py-0.5">
          <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900">
            <Link to={to} className="transition-colors hover:text-primary-600">
              {topic.title}
            </Link>
          </h3>
          <p className="mt-1 flex items-center gap-2 text-[11px] text-foreground-500">
            <span>{topic.categoryName}</span>
            <span className="text-foreground-300">·</span>
            <span>{formatViews(topic.views)} 阅读</span>
          </p>
        </div>
      </article>
    );
  }

  if (variant === "featured") {
    return (
      <article className="group grid overflow-hidden rounded-card border border-background-200 bg-background-50 md:grid-cols-2">
        <Link to={to} className="relative block h-[240px] overflow-hidden bg-background-200 md:h-full">
          <img
            src={topic.cover}
            alt={topic.coverAlt}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-md bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-flashlight-fill text-[11px]" />
            </span>
            今日热瓜
          </span>
        </Link>
        <div className="flex flex-col justify-center p-5 md:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${topic.category}`}
              className="rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800 transition-colors hover:bg-accent-200"
            >
              {topic.categoryName}
            </Link>
            <HeatTag heat={topic.heat} />
          </div>
          <h3 className="mt-3 text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
            <Link to={to} className="transition-colors hover:text-primary-600">
              {topic.title}
            </Link>
          </h3>
          <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">
            {topic.excerpt}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-user-3-line text-sm" />
              </span>
              {topic.author}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-chat-3-line text-sm" />
              </span>
              {topic.comments} 条讨论
            </span>
            <span className="flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-eye-line text-sm" />
              </span>
              {formatViews(topic.views)} 阅读
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-card border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={to} className="relative block h-[190px] overflow-hidden bg-background-200">
        <img
          src={topic.cover}
          alt={topic.coverAlt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute right-3 top-3 rounded-md bg-background-50/90 px-2 py-1 text-[11px] font-medium text-foreground-700 backdrop-blur">
          {topic.categoryName}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="line-clamp-2 text-base font-bold leading-snug text-foreground-950">
          <Link to={to} className="transition-colors hover:text-primary-600">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {topic.excerpt}
        </p>
        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-chat-3-line text-sm" />
            </span>
            {topic.comments}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-eye-line text-sm" />
            </span>
            {formatViews(topic.views)}
          </span>
          <HeatTag heat={topic.heat} />
        </div>
      </div>
    </article>
  );
}